/**
 * Unified navigation config for all store types.
 *
 * Each item supports:
 *   path             - route path
 *   label            - display label
 *   icon             - icon name (matches Layout renderIcon keys)
 *   permission       - StaffPermission string — hides if user lacks it
 *   moduleKey        - key in store.modules_enabled — hides if explicitly false
 *   storeTypes       - if set, only show for these store_type values
 *   subscriptionTier - minimum tier required ('enterprise', 'ai_pro', etc.)
 *   isCore           - true = always visible in sidebar
 *                      false/undefined = collapsed in "More" drawer
 *
 * Groups with zero visible items are automatically hidden by Layout.
 */

export const NAV_GROUPS = [
  {
    label: null,
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: 'grid', isCore: true },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        path: '/sales',
        label: 'Sales / POS',
        icon: 'dollar',
        permission: 'view_sales',
        moduleKey: 'sales',
        storeTypes: ['small_retail', 'supermarket', 'jewellery'],
        isCore: true,
      },
      {
        path: '/rentals',
        label: 'Rentals',
        icon: 'repeat',
        permission: 'view_rentals',
        moduleKey: 'rentals',
        storeTypes: ['bridal_rental'],
        isCore: true,
      },
      {
        path: '/bookings',
        label: 'Bookings',
        icon: 'calendar',
        permission: 'view_rentals',
        moduleKey: 'rentals',
        storeTypes: ['bridal_rental'],
        isCore: true,
      },
      {
        path: '/returns',
        label: 'Returns',
        icon: 'rotate-ccw',
        permission: 'process_return',
        moduleKey: 'rentals',
        storeTypes: ['bridal_rental'],
        isCore: true,
      },
      {
        // Shifts: useful but not a daily-first action for most users
        path: '/shifts',
        label: 'Shifts',
        icon: 'clock',
        permission: 'view_sales',
        storeTypes: ['small_retail', 'supermarket', 'jewellery', 'bridal_rental'],
        isCore: false,
      },
    ],
  },
  {
    label: 'Inventory',
    items: [
      {
        path: '/products',
        label: 'Products',
        icon: 'box',
        permission: 'view_products',
        isCore: true,
      },
      {
        path: '/inventory',
        label: 'Stock',
        icon: 'layers',
        permission: 'view_inventory',
        moduleKey: 'inventory',
        isCore: true,
      },
      {
        path: '/locations',
        label: 'Locations',
        icon: 'map-pin',
        permission: 'view_inventory',
        moduleKey: 'locations',
        isCore: false,
      },
      {
        path: '/orders',
        label: 'Restock Orders',
        icon: 'clipboard',
        permission: 'view_inventory',
        moduleKey: 'orders',
        isCore: false,
      },
    ],
  },
  {
    label: 'Customers',
    items: [
      {
        path: '/customers',
        label: 'Customers',
        icon: 'users',
        permission: 'view_customers',
        isCore: true,
      },
      {
        path: '/campaigns',
        label: 'Campaigns',
        icon: 'megaphone',
        permission: 'view_customers',
        isCore: false,
      },
    ],
  },
  {
    label: 'Finance',
    items: [
      {
        path: '/invoices',
        label: 'Invoices',
        icon: 'file-text',
        permission: 'view_financials',
        storeTypes: ['bridal_rental'],
        isCore: false,
      },
      {
        path: '/payments',
        label: 'Payments',
        icon: 'credit-card',
        permission: 'view_financials',
        storeTypes: ['bridal_rental'],
        isCore: false,
      },
      {
        path: '/expenses',
        label: 'Expenses',
        icon: 'receipt',
        permission: 'manage_expenses',
        isCore: false,
      },
    ],
  },
  {
    label: 'Analytics',
    items: [
      {
        path: '/reports',
        label: 'Reports',
        icon: 'bar-chart',
        permission: 'view_reports',
        isCore: true,
      },
      {
        path: '/analytics',
        label: 'AI Insights',
        icon: 'zap',
        permission: 'view_reports',
        moduleKey: 'analytics',
        isCore: false,
      },
    ],
  },
  {
    label: 'Admin',
    items: [
      {
        path: '/staff',
        label: 'Staff',
        icon: 'shield',
        permission: 'manage_staff',
        isCore: true,
      },
      {
        path: '/approvals',
        label: 'Approvals',
        icon: 'check-circle',
        permission: 'manage_staff',
        subscriptionTier: 'enterprise',
        isCore: false,
      },
    ],
  },
  {
    label: 'Settings',
    items: [
      {
        path: '/settings',
        label: 'Settings',
        icon: 'settings',
        isCore: true,
      },
    ],
  },
];

/**
 * Subscription tier order — used to compare minimum required tier.
 * Index = rank: higher = more premium.
 */
export const TIER_RANK = {
  free: 0,
  starter: 1,
  growth: 2,
  ai_pro: 3,
  enterprise: 4,
};
